import {BadgeProps, Tooltip, Badge, BoxProps, Button, Combobox as Combo, useCombobox} from '@mantine/core'
import {forwardRef, ComponentPropsWithoutRef} from 'react'
import {HiArrowRight, HiChevronDown} from 'react-icons/hi2'
import {indexBy, mapValues, pipe} from 'remeda'
import {keys} from 'shared/collection'
import {QuestionStatus, StatusMd, questionStatusMd} from 'shared/forecast/questionStatus'
import {upperFirst} from '@mantine/hooks'
import {ComboMulti, ComboMultiProps} from 'components/form/ComboMulti'

type StatusMdExt = StatusMd & {disabled?: boolean}
const statusItems = (mds: Record<string, StatusMd>) => keys(mds).map(s => questionStatusItem(mds, s))

export const questionStatusItem = (mds: Record<string, StatusMdExt>, value: string): QuestionStatusItem => {
  const md = mds[value] ?? ({} as StatusMdExt)
  return {md, value, disabled: md.disabled}
}
export type QuestionStatusItem = {md: StatusMdExt; value: string; disabled?: boolean}

interface QuestionStatusProps {
  value: string
  onChange: (value: string) => void
  readonly?: boolean
  mds: Record<string, StatusMdExt>
  hideCurrent?: boolean
  onDropdownOpen?: () => void
  size?: QuestionStatusIconProps['size']
}
export const QuestionStatusButton = ({
  value,
  onChange,
  readonly,
  mds,
  hideCurrent,
  onDropdownOpen,
  size = 'md',
  ...props
}: QuestionStatusProps) => {
  const items = statusItems(mds)
  const itemsByVal = indexBy(items, t => t.value)
  const statusCombo = useCombobox({onDropdownOpen})
  const item = itemsByVal[value]
  const nextItem = !readonly && item?.md.defaultNextStatus ? itemsByVal[item.md.defaultNextStatus] : undefined
  const nextItemLabel = nextItem ? upperFirst(nextItem.md.verb ?? nextItem.md.label ?? nextItem.value) : undefined
  return (
    <div className='flex items-center gap-2' {...props}>
      {!hideCurrent && (
        <Combo
          store={statusCombo}
          position='bottom-start'
          withinPortal
          onOptionSubmit={v => {
            statusCombo.closeDropdown()
            if (v == null) return
            onChange(v!)
          }}>
          <Combo.Target>
            <div>
              <QuestionStatusIcon
                size={size}
                data-test-id='question-status-select'
                item={item ?? {md: {color: 'blue', label: 'None', desc: 'No status set'}, value: 'none'}}
                {...(readonly ?
                  {className: 'cursor-default'}
                : {
                    className: 'cursor-pointer',
                    onClick: () => statusCombo.toggleDropdown(),
                    rightSection: <HiChevronDown />
                  })}
              />
            </div>
          </Combo.Target>
          <Combo.Dropdown>
            <Combo.Options>
              {items
                .filter(i => !i.md?.hide)
                .map(({md, value}) => (
                  <Combo.Option
                    value={value}
                    key={value}
                    data-test-id={`question-status-item-${value}`}
                    className={`cursor-pointer max-w-[40em] pt-2 ${md.disabled ? 'opacity-30' : ''}`}>
                    <QuestionStatusIcon item={{md, value}} />
                    <div className='mt-1'>{md.desc}</div>
                  </Combo.Option>
                ))}
            </Combo.Options>
          </Combo.Dropdown>
        </Combo>
      )}
      {nextItem && !nextItem.disabled && (
        <>
          <HiArrowRight className='text-gray-400' />
          <Tooltip
            label={
              <div>
                <b>Updated status to {nextItemLabel}</b>
                <p className='text-gray-300'>{nextItem.md.desc}</p>
              </div>
            }>
            <div>
              <Button
                variant='filled'
                size='compact-xs'
                radius='lg'
                color={nextItem.md.color}
                className='opacity-30 hover:opacity-100'
                onClick={() => onChange(nextItem.value)}>
                {nextItemLabel}
              </Button>
            </div>
          </Tooltip>
        </>
      )}
    </div>
  )
}

export const QuestionStatusFilter = ({
  counts: counts,
  ...props
}: {
  counts?: Partial<Record<QuestionStatus, number>>
} & Omit<ComboMultiProps<QuestionStatus>, 'md'>) => (
  <ComboMulti
    md={mapValues(questionStatusMd, (v, k) => ({...v, count: counts?.[k]}))}
    emptyLabel='any status'
    buttonProps={{__vars: {'--button-padding-x': '0px'}}}
    {...props}
  />
)

type QuestionStatusItemProps = QuestionStatusItem & Pick<BoxProps, 'className'>
const QuestionStatusItem = ({className, md, value, ...props}: QuestionStatusItemProps) => (
  <div
    {...props}
    data-test-id={`question-status-item-${value}`}
    className={`${className} max-w-[30em] ${md.disabled ? 'opacity-30' : ''}`}>
    <QuestionStatusIcon item={{md, value}} />
    <div className='mt-1'>{md.desc}</div>
  </div>
)

// @ts-ignore
QuestionStatusItem.displayName = 'QuestionStatusItem'
type QuestionStatusIconProps = {item: Omit<QuestionStatusItem, 'disabled'>} & Pick<
  ComponentPropsWithoutRef<'span'>,
  'onClick' | 'className'
> &
  Pick<BadgeProps, 'rightSection'> & {size?: 'sm' | 'md'}
export const QuestionStatusIcon = ({
  item: {
    value,
    md: {color, label: labelRaw, desc}
  },
  rightSection,
  className,
  size,
  ...props
}: QuestionStatusIconProps) => {
  const label = labelRaw ?? upperFirst(value)
  return (
    <Tooltip
      label={
        <div className='flex flex-col gap-2'>
          <div className='font-bold'>{label}</div>
          <div className='max-w-[20em]'>{desc}</div>
        </div>
      }>
      <div {...props} className={`flex items-center ${className ?? ''}`}>
        <Badge color={color} rightSection={rightSection} variant='filled' size={size}>
          {size == 'sm' ? label.toLowerCase()[0] : label}
        </Badge>
      </div>
    </Tooltip>
  )
}
